import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'
import { message } from 'antd'
import { useAuthStore } from '../../stores/authStore'
import { API_BASE_URL } from '@/services/api'

interface ContaReceber {
  id: number
  description: string
  amount: string | number
  due_date: string
  payment_date?: string
  status: string
  account_id?: number
  category_id?: number
  category_name?: string
  customer_id?: number
  client_id?: number
  notes?: string
  is_recurring?: boolean
  recurring_interval?: string
  created_at: string
  updated_at?: string
}

interface ReceivableStats {
  total_pending: number
  total_overdue: number
  total_received_this_month: number
  bills_due_this_week: number
  average_bill_amount: number
  largest_pending_bill: number
  clients_count: number
  pending_amount: number
  overdue_amount: number
}

interface Client {
  id: number
  nome: string
  documento?: string
  email?: string
  telefone?: string
  endereco?: string
  cidade?: string
  estado?: string
  cep?: string
  status: string
}

interface Product {
  id: number
  name: string
  description?: string
  code?: string
  sale_price: number  // In centavos
  current_stock: number
  track_inventory: boolean
  category_id?: number
  is_active: boolean
}

interface BillItem {
  product_id: number
  product_name?: string
  quantity: number
  unit_price: number  // In centavos
  discount: number  // In centavos
  description?: string
}

const ContasReceberPage: React.FC = () => {
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const [receivables, setReceivables] = useState<ContaReceber[]>([])
  const [stats, setStats] = useState<ReceivableStats | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [selectedItems, setSelectedItems] = useState<BillItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showReceiveModal, setShowReceiveModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedReceivable, setSelectedReceivable] = useState<ContaReceber | null>(null)
  const [selectedReceivables, setSelectedReceivables] = useState<number[]>([]) // For bulk actions
  const [searchTerm, setSearchTerm] = useState('') // For text search
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'pending' | 'overdue' | 'partial' | 'paid'>('all')

  // Advanced filters
  const [filterDateStart, setFilterDateStart] = useState('')
  const [filterDateEnd, setFilterDateEnd] = useState('')
  const [filterCategory, setFilterCategory] = useState<number | 'all'>('all')
  const [filterClient, setFilterClient] = useState<number | 'all'>('all')
  const [filterAmountMin, setFilterAmountMin] = useState('')
  const [filterAmountMax, setFilterAmountMax] = useState('')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Quick date period filter (ERP-style)
  const [selectedPeriod, setSelectedPeriod] = useState<'current_month' | 'last_3_months' | 'last_6_months' | 'current_year' | 'all'>('current_month')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Sorting - Ordenar por criação recente por padrão
  const [sortField, setSortField] = useState<'description' | 'amount' | 'due_date' | 'status' | 'created_at'>('created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    due_date: '',
    account_id: '',  // Conta bancária (obrigatório)
    customer_id: '',
    category_id: '',
    notes: '',
    is_recurring: false,
    recurring_interval: ''
  })

  const [receiveData, setReceiveData] = useState({
    account_id: '',
    payment_method: 'pix',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  })

  // Helper function to calculate date ranges for quick filters
  const getDateRange = (period: typeof selectedPeriod) => {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth() // 0-indexed

    switch(period) {
      case 'current_month':
        const firstDayOfMonth = new Date(year, month, 1)
        const lastDayOfMonth = new Date(year, month + 1, 0)
        return {
          start: firstDayOfMonth.toISOString().split('T')[0],
          end: lastDayOfMonth.toISOString().split('T')[0]
        }

      case 'last_3_months':
        const threeMonthsAgo = new Date(year, month - 2, 1) // 3 months including current
        const endOfCurrentMonth = new Date(year, month + 1, 0)
        return {
          start: threeMonthsAgo.toISOString().split('T')[0],
          end: endOfCurrentMonth.toISOString().split('T')[0]
        }

      case 'last_6_months':
        const sixMonthsAgo = new Date(year, month - 5, 1) // 6 months including current
        const endOfCurrentMonth2 = new Date(year, month + 1, 0)
        return {
          start: sixMonthsAgo.toISOString().split('T')[0],
          end: endOfCurrentMonth2.toISOString().split('T')[0]
        }

      case 'current_year':
        const firstDayOfYear = new Date(year, 0, 1)
        const lastDayOfYear = new Date(year, 11, 31)
        return {
          start: firstDayOfYear.toISOString().split('T')[0],
          end: lastDayOfYear.toISOString().split('T')[0]
        }

      case 'all':
        return { start: '', end: '' }

      default:
        return { start: '', end: '' }
    }
  }

  // Set default filter to current month on mount
  useEffect(() => {
    const defaultRange = getDateRange('current_month')
    setFilterDateStart(defaultRange.start)
    setFilterDateEnd(defaultRange.end)
  }, []) // Run only once on mount

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filterStatus, filterDateStart, filterDateEnd, filterCategory, filterClient, filterAmountMin, filterAmountMax, searchTerm])

  useEffect(() => {
    const fetchReceivablesData = async () => {
      try {
        setLoading(true)

        // Construir URL base com filtros pro backend
        const baseParams = new URLSearchParams()
        if (filterDateStart) baseParams.append('vencimento_inicio', filterDateStart)
        if (filterDateEnd) baseParams.append('vencimento_fim', filterDateEnd)
        if (filterClient !== 'all') baseParams.append('cliente_id', filterClient.toString())
        if (filterCategory !== 'all') baseParams.append('categoria_id', filterCategory.toString())

        // Buscar registros com LIMITE FIXO (ERP-style: apenas dados do período filtrado)
        // Backend fará a paginação real - frontend apenas exibe
        baseParams.append('limit', '500') // Limite razoável
        baseParams.append('offset', '0')

        const receivablesResponse = await fetch(`${API_BASE_URL}/financeiro/contas-receber?${baseParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!receivablesResponse.ok) {
          console.error('ERRO ao buscar contas a receber:', receivablesResponse.status, receivablesResponse.statusText)
          const errorText = await receivablesResponse.text()
          console.error('Detalhes do erro:', errorText)
          throw new Error(`Erro ${receivablesResponse.status}: ${receivablesResponse.statusText}`)
        }

        const rawData = await receivablesResponse.json()

        // Mapear campos do backend (português) para o frontend (inglês)
        const receivablesData = rawData.map((item: any) => ({
          id: item.id,
          description: item.descricao,
          amount: parseFloat(item.valor || 0),
          due_date: item.data_vencimento,
          payment_date: item.data_pagamento,
          status: item.status,
          account_id: item.conta?.id,  // Extrair id do objeto conta
          category_id: item.categoria?.id,
          category_name: item.categoria?.nome,
          customer_id: item.cliente?.id,
          customer_name: item.cliente?.nome,
          client_id: item.cliente?.id,  // Adicionar client_id também para compatibilidade
          notes: item.observacoes,
          is_recurring: item.recorrente || false,
          created_at: item.created_at
        }))
        setReceivables(receivablesData)
        console.log(`✅ Carregados ${receivablesData.length} registros de contas a receber`)

        // Calcular estatísticas a partir dos dados reais
        if (receivablesData && receivablesData.length > 0) {
          const pendentes = receivablesData.filter((r: any) => r.status === 'pending' || r.status === 'PENDENTE')
          const vencidas = receivablesData.filter((r: any) => r.status === 'overdue' || r.status === 'VENCIDO')
          const recebidas = receivablesData.filter((r: any) => r.status === 'paid' || r.status === 'PAGO' || r.status === 'RECEBIDO')

          const calculatedStats = {
            total_pending: pendentes.length,
            total_overdue: vencidas.length,
            total_received_this_month: recebidas.length,
            bills_due_this_week: 0,
            average_bill_amount: receivablesData.length > 0 ? receivablesData.reduce((sum: number, r: any) => sum + parseFloat(r.amount || 0), 0) / receivablesData.length : 0,
            largest_pending_bill: 0,
            clients_count: 0,
            pending_amount: pendentes.reduce((sum: number, r: any) => sum + parseFloat(r.amount || 0), 0),
            overdue_amount: vencidas.reduce((sum: number, r: any) => sum + parseFloat(r.amount || 0), 0)
          }
          setStats(calculatedStats)
        }

        // Buscar clientes
        const clientsResponse = await fetch(`${API_BASE_URL}/financeiro/clientes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (clientsResponse.ok) {
          const clientsData = await clientsResponse.json()
          setClients(clientsData)
        } else {
          console.error('Erro ao buscar clientes:', clientsResponse.statusText)
          setClients([])
        }

        // Buscar contas bancárias
        const accountsResponse = await fetch(`${API_BASE_URL}/accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (accountsResponse.ok) {
          const accountsData = await accountsResponse.json()
          setAccounts(Array.isArray(accountsData?.accounts) ? accountsData.accounts : [])
        } else {
          console.error('Erro ao buscar contas bancárias:', accountsResponse.statusText)
          setAccounts([])
        }

        // Buscar categorias
        const categoriesResponse = await fetch(`${API_BASE_URL}/categories`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (categoriesResponse.ok) {
          const categoriesData = await categoriesResponse.json()
          const cats = categoriesData.categories || categoriesData
          setCategories(Array.isArray(cats) ? cats : [])
        } else {
          console.error('Erro ao buscar categorias:', categoriesResponse.statusText)
          setCategories([])
        }

        // Buscar produtos
        const productsResponse = await fetch(`${API_BASE_URL}/produtos`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (productsResponse.ok) {
          const productsData = await productsResponse.json()
          // API retorna { produtos: [...], total, limit, offset }
          setProducts(productsData.produtos || [])
        } else {
          console.error('Erro ao buscar produtos:', productsResponse.statusText)
          setProducts([])
        }

        setError(null)
      } catch (err: any) {
        console.error('ERRO CRÍTICO:', err)
        setError(err.message || 'Erro ao carregar dados')
        setReceivables([])
        setClients([])
      } finally {
        setLoading(false)
      }
    }

    fetchReceivablesData()
  }, [filterDateStart, filterDateEnd, filterClient, filterCategory]) // Refetch when filters change

  const formatCurrency = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue || 0)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Sorting function
  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectedReceivables.length === receivables.length) {
      setSelectedReceivables([])
    } else {
      setSelectedReceivables(receivables.map(r => r.id))
    }
  }

  const handleSelectReceivable = (id: number) => {
    if (selectedReceivables.includes(id)) {
      setSelectedReceivables(selectedReceivables.filter(rId => rId !== id))
    } else {
      setSelectedReceivables([...selectedReceivables, id])
    }
  }

  // Bulk actions
  const handleBulkMarkAsReceived = async () => {
    if (selectedReceivables.length === 0) return

    if (!window.confirm(`Deseja marcar ${selectedReceivables.length} contas como recebidas?`)) {
      return
    }

    try {
      for (const id of selectedReceivables) {
        await fetch(`${API_BASE_URL}/financeiro/contas-receber/${id}/marcar-recebido`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            account_id: 1, // TODO: Get from user selection
            payment_method: 'pix',
            payment_date: new Date().toISOString().split('T')[0],
            notes: 'Recebimento em lote'
          })
        })
      }

      message.success(`${selectedReceivables.length} contas marcadas como recebidas!`)
      setSelectedReceivables([])
      window.location.reload() // Reload to refresh data
    } catch (error: any) {
      message.error('Erro ao marcar contas como recebidas: ' + error.message)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedReceivables.length === 0) return

    if (!window.confirm(`Deseja realmente excluir ${selectedReceivables.length} contas? Esta ação não pode ser desfeita.`)) {
      return
    }

    try {
      for (const id of selectedReceivables) {
        await fetch(`${API_BASE_URL}/financeiro/contas-receber/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      }

      message.success(`${selectedReceivables.length} contas excluídas!`)
      setSelectedReceivables([])
      window.location.reload() // Reload to refresh data
    } catch (error: any) {
      message.error('Erro ao excluir contas: ' + error.message)
    }
  }

  // Handle quick period filter selection
  const handlePeriodChange = (period: typeof selectedPeriod) => {
    setSelectedPeriod(period)
    const dateRange = getDateRange(period)
    setFilterDateStart(dateRange.start)
    setFilterDateEnd(dateRange.end)
    setCurrentPage(1)
  }

  // Clear all filters
  const handleClearFilters = () => {
    setFilterStatus('all')
    setSelectedPeriod('current_month') // Reset to default
    const defaultRange = getDateRange('current_month')
    setFilterDateStart(defaultRange.start)
    setFilterDateEnd(defaultRange.end)
    setFilterCategory('all')
    setFilterClient('all')
    setFilterAmountMin('')
    setFilterAmountMax('')
    setSearchTerm('')
    setCurrentPage(1)
  }

  // Product/Item management functions
  const handleAddProduct = (productId: number) => {
    const product = products.find(p => p.id === productId)
    if (!product) return

    // Check if product already added
    if (selectedItems.some(item => item.product_id === productId)) {
      message.warning('Produto já adicionado')
      return
    }

    const newItem: BillItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      unit_price: product.sale_price,
      discount: 0,
      description: product.description || ''
    }

    setSelectedItems([...selectedItems, newItem])
  }

  const handleRemoveItem = (productId: number) => {
    setSelectedItems(selectedItems.filter(item => item.product_id !== productId))
  }

  const handleUpdateItemQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) return
    setSelectedItems(selectedItems.map(item =>
      item.product_id === productId ? { ...item, quantity } : item
    ))
  }

  const handleUpdateItemDiscount = (productId: number, discount: number) => {
    if (discount < 0) return
    setSelectedItems(selectedItems.map(item =>
      item.product_id === productId ? { ...item, discount: discount * 100 } : item  // Convert to centavos
    ))
  }

  const calculateItemTotal = (item: BillItem) => {
    return (item.quantity * item.unit_price) - item.discount
  }

  const calculateTotalFromItems = () => {
    return selectedItems.reduce((sum, item) => sum + calculateItemTotal(item), 0)
  }

  const handleCreateReceivable = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Validate: must have either manual amount OR products selected
      if (selectedItems.length === 0 && !formData.amount) {
        message.error('Preencha o valor manualmente OU selecione produtos')
        return
      }

      // Calculate total from items if products were added
      let finalAmount = parseFloat(formData.amount) || 0
      if (selectedItems.length > 0) {
        finalAmount = calculateTotalFromItems() / 100  // Convert from centavos to reais
      }

      const receivableData: any = {
        tipo: 'conta_receber',
        descricao: formData.description,
        valor: finalAmount,
        data_vencimento: formData.due_date,
        conta_id: formData.account_id ? parseInt(formData.account_id) : null,
        cliente_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        categoria_id: formData.category_id ? parseInt(formData.category_id) : null,
        observacoes: formData.notes
      }

      // Add items if products were selected
      if (selectedItems.length > 0) {
        receivableData.items = selectedItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          description: item.description
        }))
      }

      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(receivableData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao criar conta a receber')
      }

      // Reset form and close modal
      setFormData({
        description: '',
        amount: '',
        due_date: '',
        account_id: '',
        customer_id: '',
        category_id: '',
        notes: '',
        is_recurring: false,
        recurring_interval: ''
      })
      setSelectedItems([])
      setShowCreateModal(false)

      // Show success message with instructions
      message.success({
        content: 'Conta a receber criada com sucesso! Ela aparecerá no topo da lista com um badge "NOVO".',
        duration: 5
      })

      // Refresh data
      window.location.reload()

    } catch (err: any) {
      message.error(err.message || 'Erro ao criar conta')
      setError(err.message || 'Erro ao criar conta')
    }
  }

  const handleReceivePayment = (receivable: ContaReceber) => {
    setSelectedReceivable(receivable)
    setReceiveData({
      account_id: '',
      payment_method: 'pix',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    })
    setShowReceiveModal(true)
  }

  const handleEditReceivable = (receivable: ContaReceber) => {
    setSelectedReceivable(receivable)
    const amount = parseFloat(receivable.amount?.toString() || '0')

    setFormData({
      description: receivable.description,
      amount: amount.toString(),
      due_date: receivable.due_date,
      account_id: receivable.account_id?.toString() || '',
      customer_id: receivable.customer_id?.toString() || '',
      category_id: receivable.category_id?.toString() || '',
      notes: receivable.notes || '',
      is_recurring: receivable.is_recurring || false,
      recurring_interval: receivable.recurring_interval || ''
    })
    setShowEditModal(true)
  }

  const processReceivePayment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedReceivable) return

    try {
      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber/${selectedReceivable.id}/marcar-recebido`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: receiveData.account_id ? parseInt(receiveData.account_id) : null,
          payment_method: receiveData.payment_method,
          payment_date: receiveData.payment_date,
          notes: receiveData.notes
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao processar recebimento')
      }

      // Reset modal
      setShowReceiveModal(false)
      setSelectedReceivable(null)

      // Show success message
      message.success('Recebimento processado com sucesso!')

      // Refresh data
      window.location.reload()

    } catch (error: any) {
      console.error('Erro ao processar recebimento:', error)
      message.error(error.message || 'Erro ao processar recebimento')
      setError(error.message || 'Erro ao processar recebimento')
    }
  }

  const handleUpdateReceivable = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedReceivable) return

    try {
      const receivableData = {
        tipo: 'conta_receber',
        descricao: formData.description,
        valor: parseFloat(formData.amount),
        data_vencimento: formData.due_date,
        conta_id: formData.account_id ? parseInt(formData.account_id) : null,
        cliente_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        categoria_id: formData.category_id ? parseInt(formData.category_id) : null,
        observacoes: formData.notes
      }

      const response = await fetch(`${API_BASE_URL}/financeiro/contas-receber/${selectedReceivable.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(receivableData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao atualizar conta')
      }

      // Reset form and close modal
      setFormData({
        description: '',
        amount: '',
        due_date: '',
        account_id: '',
        customer_id: '',
        category_id: '',
        notes: '',
        is_recurring: false,
        recurring_interval: ''
      })
      setShowEditModal(false)
      setSelectedReceivable(null)

      // Show success message
      message.success('Conta atualizada com sucesso!')

      // Refresh data
      window.location.reload()

    } catch (err: any) {
      message.error(err.message || 'Erro ao atualizar conta')
      setError(err.message || 'Erro ao atualizar conta')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'open': case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'overdue': return 'bg-red-100 text-red-800'
      case 'partial': return 'bg-blue-100 text-blue-800'
      case 'cancelled': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'Recebido'
      case 'open': case 'pending': return 'Pendente'
      case 'overdue': return 'Vencido'
      case 'partial': return 'Parcial'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date()
    const due = new Date(dueDate)
    const diffTime = due.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const isRecentlyCreated = (createdAt: string) => {
    const now = new Date()
    const created = new Date(createdAt)
    const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60)
    return diffHours < 24 // Criado nas últimas 24h
  }

  // Dados reais - SEM MOCKS
  const displayStats = stats || {
    total_pending: 0,
    total_overdue: 0,
    total_received_this_month: 0,
    bills_due_this_week: 0,
    average_bill_amount: 0,
    largest_pending_bill: 0,
    clients_count: 0,
    pending_amount: 0,
    overdue_amount: 0
  }
  const displayReceivables = Array.isArray(receivables) ? receivables : []
  const displayClients = Array.isArray(clients) ? clients : []

  // Apply all filters
  const filteredAndSearched = displayReceivables.filter(receivable => {
    // Filter by status
    const matchesStatus = filterStatus === 'all' || receivable.status === filterStatus

    // Filter by search term
    const matchesSearch = searchTerm === '' ||
      receivable.description?.toLowerCase().includes(searchTerm.toLowerCase())

    // Filter by date range
    const matchesDateStart = !filterDateStart ||
      (receivable.due_date && new Date(receivable.due_date) >= new Date(filterDateStart))
    const matchesDateEnd = !filterDateEnd ||
      (receivable.due_date && new Date(receivable.due_date) <= new Date(filterDateEnd))

    // Filter by category
    const matchesCategory = filterCategory === 'all' || receivable.category_id === filterCategory

    // Filter by client
    const matchesClient = filterClient === 'all' || receivable.client_id === filterClient

    // Filter by amount range
    const receivableAmount = parseFloat(receivable.amount?.toString() || '0')
    const matchesAmountMin = !filterAmountMin || receivableAmount >= parseFloat(filterAmountMin)
    const matchesAmountMax = !filterAmountMax || receivableAmount <= parseFloat(filterAmountMax)

    return matchesStatus && matchesSearch && matchesDateStart && matchesDateEnd &&
           matchesCategory && matchesClient && matchesAmountMin && matchesAmountMax
  })

  // Apply sorting
  const sortedReceivables = [...filteredAndSearched].sort((a, b) => {
    let aValue: any, bValue: any

    switch (sortField) {
      case 'description':
        aValue = a.description || ''
        bValue = b.description || ''
        break
      case 'amount':
        aValue = parseFloat(a.amount?.toString() || '0')
        bValue = parseFloat(b.amount?.toString() || '0')
        break
      case 'due_date':
        aValue = new Date(a.due_date || '').getTime()
        bValue = new Date(b.due_date || '').getTime()
        break
      case 'status':
        aValue = a.status || ''
        bValue = b.status || ''
        break
      case 'created_at':
        aValue = new Date(a.created_at || '').getTime()
        bValue = new Date(b.created_at || '').getTime()
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Apply pagination
  const totalPages = Math.ceil(sortedReceivables.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedReceivables = sortedReceivables.slice(startIndex, endIndex)

  // For backward compatibility
  const filteredReceivables = paginatedReceivables

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar contas a receber</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="w-7 h-7 text-green-600" />
            Contas a Receber
          </h1>
          <p className="text-gray-600">Gestão completa de recebimentos e clientes</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/financeiro/contas-receber/dashboard')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <ChartBarIcon className="w-5 h-5" />
            Ver Dashboard
          </button>
          <button
            onClick={() => {
              setSelectedItems([])
              setShowCreateModal(true)
            }}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="w-5 h-5" />
            Nova Conta
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Pendentes</p>
              <p className="text-2xl font-bold">{displayStats.total_pending}</p>
              <p className="text-sm">{formatCurrency(displayStats.pending_amount)}</p>
            </div>
            <ClockIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Vencidas</p>
              <p className="text-2xl font-bold">{displayStats.total_overdue}</p>
              <p className="text-sm">{formatCurrency(displayStats.overdue_amount)}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Recebidas este Mês</p>
              <p className="text-2xl font-bold">{displayStats.total_received_this_month}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Ticket Médio</p>
              <p className="text-2xl font-bold">{formatCurrency(displayStats.average_bill_amount)}</p>
            </div>
            <CurrencyDollarIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Quick Period Filters (ERP-style) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Filtro de Período</h3>
            <p className="text-xs text-gray-500">Selecione um período para visualizar as contas</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handlePeriodChange('current_month')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'current_month'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Este Mês
            </button>
            <button
              onClick={() => handlePeriodChange('last_3_months')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'last_3_months'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Últimos 3 Meses
            </button>
            <button
              onClick={() => handlePeriodChange('last_6_months')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'last_6_months'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Últimos 6 Meses
            </button>
            <button
              onClick={() => handlePeriodChange('current_year')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'current_year'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Este Ano
            </button>
            <button
              onClick={() => {
                if (window.confirm('⚠️ ATENÇÃO: Carregar todos os períodos pode ser lento se você tiver muitos registros. Deseja continuar?')) {
                  handlePeriodChange('all')
                }
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all border-2 ${
                selectedPeriod === 'all'
                  ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                  : 'bg-white text-orange-600 border-orange-600 hover:bg-orange-50'
              }`}
            >
              Todos os Períodos
            </button>
          </div>
        </div>
        {selectedPeriod !== 'all' && filterDateStart && filterDateEnd && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-2">
            <CalendarIcon className="w-4 h-4 text-blue-600" />
            <span>
              Mostrando registros de <strong>{new Date(filterDateStart).toLocaleDateString('pt-BR')}</strong> até <strong>{new Date(filterDateEnd).toLocaleDateString('pt-BR')}</strong>
            </span>
          </div>
        )}
        {selectedPeriod === 'all' && (
          <div className="mt-3 flex items-center gap-2 text-sm text-orange-700 bg-orange-50 border border-orange-300 rounded-lg p-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-orange-600" />
            <span>
              <strong>Modo "Todos os Períodos" ativo:</strong> Carregando até 500 registros mais recentes. Use filtros de data para melhor performance.
            </span>
          </div>
        )}
      </div>

      {/* Filter and Receivables Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">Contas a Receber</h3>
              {sortField === 'created_at' && sortDirection === 'desc' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                  Mais Recentes Primeiro ↓
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Buscar por descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
              />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">Todas</option>
                <option value="open">Em Aberto</option>
                <option value="pending">Pendentes</option>
                <option value="overdue">Vencidas</option>
                <option value="partial">Parciais</option>
                <option value="paid">Recebidas</option>
              </select>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FunnelIcon className="w-4 h-4" />
                Filtros Avançados
              </button>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Data Início
                  </label>
                  <input
                    type="date"
                    value={filterDateStart}
                    onChange={(e) => setFilterDateStart(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">Todas</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Cliente
                  </label>
                  <select
                    value={filterClient}
                    onChange={(e) => setFilterClient(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">Todos</option>
                    {/* TODO: Add clients list when available */}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Valor Mínimo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="R$ 0,00"
                    value={filterAmountMin}
                    onChange={(e) => setFilterAmountMin(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Valor Máximo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="R$ 0,00"
                    value={filterAmountMax}
                    onChange={(e) => setFilterAmountMax(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          )}

          {/* Bulk Actions Bar */}
          {selectedReceivables.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-medium text-blue-900">
                {selectedReceivables.length} {selectedReceivables.length === 1 ? 'conta selecionada' : 'contas selecionadas'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkMarkAsReceived}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  Marcar como Recebidas
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Excluir Selecionadas
                </button>
                <button
                  onClick={() => setSelectedReceivables([])}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedReceivables.length === filteredReceivables.length && filteredReceivables.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th
                  onClick={() => handleSort('description')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Descrição
                    {sortField === 'description' && (
                      <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('amount')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Valor
                    {sortField === 'amount' && (
                      <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('due_date')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Vencimento
                    {sortField === 'due_date' && (
                      <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('status')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'status' && (
                      <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReceivables.map((receivable) => {
                const daysUntilDue = getDaysUntilDue(receivable.due_date)
                const isSelected = selectedReceivables.includes(receivable.id)
                const isNew = isRecentlyCreated(receivable.created_at)
                return (
                  <tr key={receivable.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : isNew ? 'bg-green-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectReceivable(receivable.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {receivable.customer_name || `Cliente ${receivable.customer_id || receivable.client_id || '-'}`}
                          </div>
                          <div className="text-sm text-gray-500">ID: {receivable.customer_id || receivable.client_id || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-900">{receivable.description}</div>
                        {isNew && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                            NOVO
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">Categoria {receivable.category_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(receivable.amount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(receivable.due_date)}</div>
                      {receivable.status === 'pending' && (
                        <div className={`text-sm ${daysUntilDue < 0 ? 'text-red-600' : daysUntilDue <= 7 ? 'text-yellow-600' : 'text-gray-500'}`}>
                          {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} dias em atraso` :
                           daysUntilDue === 0 ? 'Vence hoje' :
                           `${daysUntilDue} dias`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(receivable.status)}`}>
                        {getStatusLabel(receivable.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {receivable.status === 'pending' && (
                          <button
                            onClick={() => handleReceivePayment(receivable)}
                            className="text-green-600 hover:text-green-900 text-sm"
                          >
                            Receber
                          </button>
                        )}
                        <button
                          onClick={() => handleEditReceivable(receivable)}
                          className="text-blue-600 hover:text-blue-900 text-sm"
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">
                Mostrando {startIndex + 1} a {Math.min(endIndex, sortedReceivables.length)} de {sortedReceivables.length} registros
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value={10}>10 por página</option>
                <option value={25}>25 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Primeira
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm font-medium rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Última
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Clients Overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Clientes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayClients.slice(0, 6).map((client) => (
            <div key={client.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{client.nome}</h4>
                <div className={`w-3 h-3 rounded-full ${client.status === 'ATIVO' ? 'bg-green-400' : 'bg-gray-300'}`}></div>
              </div>
              <div className="text-sm text-gray-600">
                Status: {client.status}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Nova Conta a Receber</h3>
            <form className="space-y-4" onSubmit={handleCreateReceivable}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Descrição da conta"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conta Bancária *
                </label>
                <select
                  value={formData.account_id}
                  onChange={(e) => setFormData({...formData, account_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Selecione a conta bancária</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatCurrency(account.balance || 0)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Conta para crédito do recebimento
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <select
                  value={formData.customer_id}
                  onChange={(e) => setFormData({...formData, customer_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Selecione um cliente</option>
                  {displayClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nome}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {displayClients.length > 0
                    ? `${displayClients.length} cliente(s) disponível(is)`
                    : 'Nenhum cliente cadastrado'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor {selectedItems.length > 0 && <span className="text-xs text-gray-500">(Calculado automaticamente)</span>}
                  </label>
                  {selectedItems.length > 0 ? (
                    <div className="w-full border border-green-300 bg-green-50 rounded-lg px-3 py-2 flex items-center justify-between">
                      <span className="text-lg font-bold text-green-700">
                        {formatCurrency(calculateTotalFromItems() / 100)}
                      </span>
                      <span className="text-xs text-green-600">Calculado dos produtos</span>
                    </div>
                  ) : (
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="0,00"
                      required
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedItems.length > 0
                      ? "Valor calculado a partir dos produtos selecionados"
                      : "Preencha o valor manualmente OU selecione produtos abaixo"
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Vencimento
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Selecione uma categoria (opcional)</option>
                  {categories
                    .filter((cat) => cat.type === 'income') // Filtrar apenas categorias de receita
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nome || cat.name}
                      </option>
                    ))
                  }
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {categories.filter((cat) => cat.type === 'income').length > 0
                    ? `${categories.filter((cat) => cat.type === 'income').length} categoria(s) de receita disponível(is)`
                    : 'Nenhuma categoria de receita cadastrada'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                  placeholder="Observações adicionais..."
                ></textarea>
              </div>

              {/* Product Selection Section */}
              <div className="border-t pt-4 mt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Dica:</strong> Você pode preencher o valor manualmente acima <strong>OU</strong> selecionar produtos abaixo.
                    Se selecionar produtos, o valor será calculado automaticamente.
                  </p>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produtos/Serviços (Opcional)
                </label>
                <div className="flex gap-2 mb-3">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddProduct(parseInt(e.target.value))
                        e.target.value = ''
                      }
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Selecione um produto para adicionar</option>
                    {products
                      .filter(p => p.is_active === true)
                      .map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - {formatCurrency(product.sale_price / 100)} (Estoque: {product.current_stock})
                        </option>
                      ))
                    }
                  </select>
                </div>

                {/* Selected Products Table */}
                {selectedItems.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Produto</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Qtd</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Preço Un.</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Desconto</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Total</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedItems.map(item => (
                          <tr key={item.product_id}>
                            <td className="px-3 py-2 text-sm text-gray-900">{item.product_name}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={item.quantity}
                                onChange={(e) => handleUpdateItemQuantity(item.product_id, parseFloat(e.target.value))}
                                className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(item.unit_price / 100)}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.discount / 100}
                                onChange={(e) => handleUpdateItemDiscount(item.product_id, parseFloat(e.target.value))}
                                className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                                placeholder="R$ 0,00"
                              />
                            </td>
                            <td className="px-3 py-2 text-sm font-medium text-gray-900">
                              {formatCurrency(calculateItemTotal(item) / 100)}
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(item.product_id)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remover
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={4} className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                            Total:
                          </td>
                          <td colSpan={2} className="px-3 py-2 text-sm font-bold text-green-600">
                            {formatCurrency(calculateTotalFromItems() / 100)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  {selectedItems.length > 0
                    ? `${selectedItems.length} produto(s) selecionado(s). O valor será calculado automaticamente.`
                    : 'Adicione produtos para que o valor seja calculado automaticamente, ou insira manualmente acima.'}
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Criar Conta
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItems([])
                    setShowCreateModal(false)
                  }}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Receive Payment Modal */}
      {showReceiveModal && selectedReceivable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Receber Pagamento</h3>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Conta selecionada:</p>
              <p className="font-medium">{selectedReceivable.description}</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(selectedReceivable.amount)}</p>
            </div>

            <form className="space-y-4" onSubmit={processReceivePayment}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conta Bancária para Crédito
                </label>
                <select
                  value={receiveData.account_id}
                  onChange={(e) => setReceiveData({...receiveData, account_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Selecione a conta bancária</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatCurrency(account.balance || 0)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forma de Recebimento
                </label>
                <select
                  value={receiveData.payment_method}
                  onChange={(e) => setReceiveData({...receiveData, payment_method: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="pix">PIX</option>
                  <option value="debit_card">Débito</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="cash">Dinheiro</option>
                  <option value="bank_transfer">Transferência Bancária</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data do Recebimento
                </label>
                <input
                  type="date"
                  value={receiveData.payment_date}
                  onChange={(e) => setReceiveData({...receiveData, payment_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações do Recebimento
                </label>
                <textarea
                  value={receiveData.notes}
                  onChange={(e) => setReceiveData({...receiveData, notes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                  placeholder="Observações sobre o recebimento..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Confirmar Recebimento
                </button>
                <button
                  type="button"
                  onClick={() => setShowReceiveModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedReceivable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Editar Conta a Receber</h3>
            <form className="space-y-4" onSubmit={handleUpdateReceivable}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Descrição da conta"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conta Bancária *
                </label>
                <select
                  value={formData.account_id}
                  onChange={(e) => setFormData({...formData, account_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione a conta bancária</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatCurrency(account.balance || 0)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Conta para crédito do recebimento
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <select
                  value={formData.customer_id}
                  onChange={(e) => setFormData({...formData, customer_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione um cliente</option>
                  {displayClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Vencimento
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione uma categoria (opcional)</option>
                  {categories
                    .filter((cat) => cat.type === 'income') // Filtrar apenas categorias de receita
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nome || cat.name}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Observações adicionais..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Salvar Alterações
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContasReceberPage